/**
 * spanReplaceLockHighlight.ts — PLAT-10214 (T6, epic PLAT-10209)
 *
 * The original-side **lock** highlight for span replace (epic D3): while the
 * `ReplaceSpanEditor` is open over a translated span, the mapped ORIGINAL words
 * (the union of the covered fragments' `originalIndices`) light up + lock in the
 * left pane, so the reviewer sees exactly which source words the correction
 * covers and confirms against them.
 *
 * Reuses the CSS Custom Highlight API mechanism (and the per-pane range
 * provenance that keeps it scoped to one pane — CLAUDE.md #12) from
 * `linkedCrossLanguageHighlight.ts`, with a DEDICATED highlight name + hue so it
 * never collides with the transient linked-selection highlight (soft blue),
 * playback / search (gold), or the changed-phrase mark. A solid gold-tinted
 * lock reads as "this is the active edit target".
 */

import { applyLinkedHighlight } from './linkedCrossLanguageHighlight';

/** Highlight name for the original-pane span-replace lock. Distinct from the
 *  linked-selection names so the two coexist without overwriting each other. */
export const SPAN_REPLACE_LOCK_HIGHLIGHT = 'transcript-span-replace-lock';

/**
 * Global `::highlight()` rule for the lock. A firmer gold tint (the active
 * replace target, epic D3) — distinct from the linked-selection soft blue and
 * from the playing-word solid gold fill. Mount once via `createGlobalStyle`
 * while the workspace is open.
 */
export const spanReplaceLockCss = `
  ::highlight(${SPAN_REPLACE_LOCK_HIGHLIGHT}) {
    background-color: rgba(223, 182, 21, 0.32);
  }
`;

/**
 * Paint the lock over `initialIndices` in the original `container` (range
 * provenance keeps it scoped to that pane). Pass an empty set to clear it.
 */
export function applySpanReplaceLock(
  container: HTMLElement | null,
  initialIndices: ReadonlySet<number>,
): void {
  if (!container) return;
  // PLAT-10611 (F5) — adapt to `applyLinkedHighlight`'s dual-key signature. The
  // lock is NOT in the F5 reader re-key set (it paints the covered SOURCE indices
  // in the original pane, which stays in story-index space), so it keeps keying
  // on the retained `data-initial-index` (an integer-only mirror).
  applyLinkedHighlight(
    container,
    { mirrorIds: new Set<string>(), mirrorInts: initialIndices },
    SPAN_REPLACE_LOCK_HIGHLIGHT,
  );
}

/** Clear the lock highlight. Idempotent; safe when the API is unsupported. */
export function clearSpanReplaceLock(container: HTMLElement | null): void {
  applySpanReplaceLock(container, new Set());
}
